import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Calendar, User, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { BlogMediaRenderer } from '@/components/BlogMediaRenderer';
import { removeJsonLd, upsertCanonical, upsertMetaTag } from '@/lib/seoMeta';
import { prerenderData } from '@/generated/prerenderData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_BLOG_CARD_IMAGE = 'https://customer-assets.emergentagent.com/job_gib-bridal-list/artifacts/pjamqf3f_2377-aniapaul-websized-5.jpg';

const isVideoUrl = (value) => /\.(mp4|mov|webm|ogg)(\?|#|$)/i.test(String(value || ''));

const resolveBlogCardMedia = (post) => {
  const featuredMediaUrl = post?.featured_media_url || '';
  const featuredMediaType = String(post?.featured_media_type || '').toLowerCase();
  const featuredImage = post?.featured_image || '';
  const galleryImage = Array.isArray(post?.gallery_images)
    ? post.gallery_images.find((image) => Boolean(image))
    : '';
  const videoUrl = post?.video_url || '';

  const featuredMediaLooksLikeImage = featuredMediaUrl && !isVideoUrl(featuredMediaUrl);
  const featuredMediaLooksLikeVideo = featuredMediaType === 'video' || isVideoUrl(featuredMediaUrl);

  const preferredImage =
    featuredImage ||
    (featuredMediaType === 'image' ? featuredMediaUrl : '') ||
    galleryImage ||
    (featuredMediaLooksLikeImage ? featuredMediaUrl : '');

  if (preferredImage) {
    return { mediaUrl: preferredImage, mediaType: 'image' };
  }

  if (videoUrl || featuredMediaLooksLikeVideo) {
    return { mediaUrl: videoUrl || featuredMediaUrl, mediaType: 'video' };
  }

  return { mediaUrl: DEFAULT_BLOG_CARD_IMAGE, mediaType: 'image' };
};

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const origin = window.location.origin;
    document.title = 'Gibraltar Wedding Blog | Planning Advice & Real Tips';
    upsertMetaTag(
      'name',
      'description',
      'Read Gibraltar Wedding Expo blog articles for planning advice, supplier tips, real wedding ideas, and practical guidance for destination couples.'
    );
    upsertMetaTag('name', 'robots', 'index, follow');
    upsertCanonical(`${origin}/blog`);
    upsertMetaTag('property', 'og:title', 'Gibraltar Wedding Blog | Gibraltar Wedding Expo');
    upsertMetaTag('property', 'og:description', 'Wedding planning advice, supplier insights, and inspiration for getting married in Gibraltar.');
    upsertMetaTag('property', 'og:url', `${origin}/blog`);
    upsertMetaTag('property', 'og:type', 'website');
    removeJsonLd('blog-post-jsonld');
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
      setPosts(prerenderData?.blogPosts || []);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24" data-testid="blog-page">
      <div className="container-wedding">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="caption mb-4">Wedding Inspiration</p>
          <h1 className="heading-xl mb-4">Blog & Tips</h1>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Expert advice, inspiration, and tips for planning your perfect Gibraltar wedding.
          </p>
        </div>

        {/* Posts Grid */}
        {posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-stone-500 text-lg">No blog posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              (() => {
                const cardMedia = resolveBlogCardMedia(post);
                return (
              <article
                key={post.id}
                className="bg-white rounded-sm border border-stone-100 card-shadow overflow-hidden group animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`blog-post-${post.id}`}
              >
                {/* Featured Image */}
                <Link to={`/blog/${post.slug}`} className="block image-zoom aspect-video">
                  <BlogMediaRenderer
                    mediaUrl={cardMedia.mediaUrl}
                    mediaType={cardMedia.mediaType}
                    alt={post.title}
                    variant="card"
                    imageClassName="w-full h-full object-cover grayscale-[10%] group-hover:grayscale-0"
                    videoClassName="w-full h-full object-cover"
                    testIdPrefix={`blog-card-media-${post.id}`}
                  />
                </Link>

                {/* Content */}
                <div className="p-6">
                  <span className="caption text-[#D4AF37]">{post.category}</span>
                  <Link to={`/blog/${post.slug}`}>
                    <h2 className="heading-md mt-2 mb-3 group-hover:text-[#5C0120] transition-colors duration-200">
                      {post.title}
                    </h2>
                  </Link>
                  <p className="text-stone-600 text-sm line-clamp-3 mb-4">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                    <div className="flex items-center gap-4 text-xs text-stone-500">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {post.author_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(post.created_at), 'MMM d, yyyy')}
                      </span>
                    </div>
                  </div>
                </div>
              </article>
                );
              })()
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
